import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
  Input,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Textarea,
  Label,
} from "@homy/ui";
import {
  Store,
  Search,
  RefreshCw,
  ShieldCheck,
  Award,
  Star,
  MapPin,
  Wrench,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Phone,
  Mail,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";
import {
  listMarketplaceVendors,
  updateVendorVerification,
} from "../lib/api/marketplaceClient";

function VendorVerificationBadge({ tier }) {
  const t = (tier || "SOURCED").toUpperCase();
  switch (t) {
    case "PREMIUM":
    case "PRO":
      return (
        <Badge
          variant="outline"
          className="border-amber-500/40 bg-amber-500/15 text-amber-400 font-mono text-[11px] font-semibold gap-1"
        >
          <Award className="h-3 w-3 text-amber-400" />
          PREMIUM (PRO)
        </Badge>
      );
    case "VERIFIED":
      return (
        <Badge
          variant="outline"
          className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-mono text-[11px] font-semibold gap-1"
        >
          <ShieldCheck className="h-3 w-3 text-emerald-400" />
          VERIFIED
        </Badge>
      );
    case "CLAIMED":
    case "BASIC":
      return (
        <Badge
          variant="outline"
          className="border-sky-500/30 bg-sky-500/10 text-sky-400 font-mono text-[11px]"
        >
          CLAIMED (BASIC)
        </Badge>
      );
    case "SOURCED":
    case "UNVERIFIED":
    default:
      return (
        <Badge
          variant="outline"
          className="border-slate-500/30 bg-slate-500/10 text-muted-foreground font-mono text-[11px]"
        >
          SOURCED (UNVERIFIED)
        </Badge>
      );
  }
}

export function VendorsMarketplacePage() {
  const queryClient = useQueryClient();

  // Filters State
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [communeFilter, setCommuneFilter] = useState("ALL");
  const [tierFilter, setTierFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  // Moderation Dialog State
  const [moderationVendor, setModerationVendor] = useState(null);
  const [selectedTier, setSelectedTier] = useState("VERIFIED");
  const [moderationNote, setModerationNote] = useState("");

  // Query: Marketplace Vendors
  const {
    data: vendorsData,
    isLoading: vendorsLoading,
    isFetching,
    refetch: refetchVendors,
  } = useQuery({
    queryKey: [
      "platform-marketplace-vendors",
      searchTerm,
      categoryFilter,
      communeFilter,
      tierFilter,
      currentPage,
    ],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (categoryFilter !== "ALL") params.category = categoryFilter;
      if (communeFilter !== "ALL") params.commune = communeFilter;
      if (tierFilter !== "ALL") params.verificationLevel = tierFilter;

      return await listMarketplaceVendors(params);
    },
    refetchInterval: 30000,
  });

  // Mutation: Verification Update
  const verificationMutation = useMutation({
    mutationFn: ({ vendorId, tier, note }) =>
      updateVendorVerification(vendorId, {
        verificationLevel: tier,
        note,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-marketplace-vendors"] });
      setModerationVendor(null);
      setModerationNote("");
    },
  });

  const handleOpenModeration = (vendor) => {
    setModerationVendor(vendor);
    setSelectedTier(vendor.verificationLevel || "VERIFIED");
    setModerationNote("");
  };

  const handleConfirmModeration = () => {
    if (!moderationVendor) return;
    verificationMutation.mutate({
      vendorId: moderationVendor.id,
      tier: selectedTier,
      note: moderationNote.trim() || undefined,
    });
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setCategoryFilter("ALL");
    setCommuneFilter("ALL");
    setTierFilter("ALL");
    setCurrentPage(1);
  };

  const vendors = vendorsData?.items || [];
  const totalVendors = vendorsData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalVendors / pageSize));

  return (
    <PageContainer className="max-w-7xl mx-auto space-y-6">
      {/* Top Header */}
      <PageHeader
        title="Vendors Marketplace Hub"
        description="Sovereign directory moderation for certified Chilean contractors, emergency trades, and profile verification tiers."
        actions={
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => refetchVendors()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {/* Filter Toolbar */}
      <div className="bg-card border border-border p-4 rounded-xl space-y-3">
        <div className="flex flex-col md:flex-row items-center gap-3">
          {/* Search Input */}
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by vendor name, company, email, or Chilean RUT..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 bg-input"
            />
          </div>

          {(searchTerm ||
            categoryFilter !== "ALL" ||
            communeFilter !== "ALL" ||
            tierFilter !== "ALL") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              className="text-xs text-muted-foreground hover:text-foreground shrink-0"
            >
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>

        {/* Dropdowns Row */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border/50 text-xs">
          {/* Category Filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">Category:</span>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-border bg-input px-2.5 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Categories</option>
              <option value="gasfiteria">Gasfitería (Plumbing)</option>
              <option value="electricista">Electricidad (Electrical)</option>
              <option value="cerrajero">Cerrajería (Locksmith)</option>
              <option value="maestro general">Maestro General (Handyman)</option>
              <option value="pintura">Pintura (Painting)</option>
            </select>
          </div>

          {/* Commune Filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">Commune:</span>
            <select
              value={communeFilter}
              onChange={(e) => {
                setCommuneFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-border bg-input px-2.5 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Comunas</option>
              <option value="Providencia">Providencia</option>
              <option value="Las Condes">Las Condes</option>
              <option value="Santiago">Santiago</option>
              <option value="Ñuñoa">Ñuñoa</option>
              <option value="Vitacura">Vitacura</option>
              <option value="Lo Barnechea">Lo Barnechea</option>
            </select>
          </div>

          {/* Verification Tier Filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">Tier:</span>
            <select
              value={tierFilter}
              onChange={(e) => {
                setTierFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-border bg-input px-2.5 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Verification Tiers</option>
              <option value="PREMIUM">PREMIUM (PRO)</option>
              <option value="VERIFIED">VERIFIED</option>
              <option value="CLAIMED">CLAIMED (BASIC)</option>
              <option value="SOURCED">SOURCED (UNVERIFIED)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vendors Table Card */}
      <Card className="border-border">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Store className="h-4 w-4 text-primary" /> Vendor Directory & Reputation
          </CardTitle>
          <span className="text-xs font-mono text-muted-foreground">
            {totalVendors} total registered profiles
          </span>
        </CardHeader>
        <CardContent>
          {vendorsLoading ? (
            <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-2">
              <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              <p>Loading marketplace vendors directory...</p>
            </div>
          ) : vendors.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground space-y-2">
              <Store className="h-8 w-8 mx-auto text-muted-foreground/60" />
              <p className="font-medium text-foreground">No Vendors Found</p>
              <p className="text-xs">No vendor profiles match the selected filters or search terms.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendor & Tax Identity</TableHead>
                    <TableHead>Categories & Communes</TableHead>
                    <TableHead>Verification Status</TableHead>
                    <TableHead>Reputation & Rating</TableHead>
                    <TableHead>Platform Score</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((vendor) => {
                    const categories = vendor.categories || [];
                    const communes = vendor.communes || [];

                    return (
                      <TableRow key={vendor.id} className="hover:bg-muted/30">
                        {/* Name & Company */}
                        <TableCell className="space-y-1 max-w-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-foreground">
                              {vendor.name}
                            </span>
                            {vendor.emergencyAvailable && (
                              <Badge
                                variant="outline"
                                className="border-rose-500/40 bg-rose-500/10 text-rose-400 text-[10px] font-mono"
                              >
                                24/7
                              </Badge>
                            )}
                          </div>
                          {vendor.companyName && (
                            <p className="text-xs text-muted-foreground">{vendor.companyName}</p>
                          )}
                          <div className="flex items-center gap-3 text-[11px] text-muted-foreground font-mono">
                            {vendor.rut && <span>RUT: {vendor.rut}</span>}
                            {vendor.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {vendor.phone}
                              </span>
                            )}
                          </div>
                        </TableCell>

                        {/* Categories & Communes */}
                        <TableCell className="space-y-1.5 max-w-xs">
                          <div className="flex flex-wrap gap-1">
                            {categories.slice(0, 3).map((cat, i) => (
                              <Badge key={i} variant="secondary" className="text-[10px]">
                                {cat}
                              </Badge>
                            ))}
                            {categories.length > 3 && (
                              <span className="text-[10px] text-muted-foreground">
                                +{categories.length - 3}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="truncate">
                              {communes.slice(0, 2).join(", ")}
                              {communes.length > 2 ? ` (+${communes.length - 2})` : ""}
                            </span>
                          </div>
                        </TableCell>

                        {/* Verification Status */}
                        <TableCell>
                          <VendorVerificationBadge tier={vendor.verificationLevel} />
                        </TableCell>

                        {/* Reputation / Rating */}
                        <TableCell className="space-y-1">
                          <div className="flex items-center gap-1.5 text-xs font-mono">
                            <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                            <span className="font-bold text-foreground">
                              {vendor.homyRating
                                ? vendor.homyRating.toFixed(1)
                                : vendor.googleRating
                                ? vendor.googleRating.toFixed(1)
                                : "N/A"}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              ({(vendor.homyReviewCount || 0) + (vendor.googleUserRatingsTotal || 0)} reviews)
                            </span>
                          </div>
                        </TableCell>

                        {/* Ranking Score */}
                        <TableCell>
                          <span className="text-sm font-bold font-mono text-primary">
                            {(vendor.rankingScore || 0).toFixed(1)}
                          </span>
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs hover:text-primary hover:border-primary/50"
                            onClick={() => handleOpenModeration(vendor)}
                          >
                            Moderate Tier
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-border mt-4 text-xs text-muted-foreground">
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Moderation Modal Dialog */}
      <Dialog
        open={Boolean(moderationVendor)}
        onOpenChange={(open) => !open && setModerationVendor(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" /> Vendor Tier Moderation
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Update verification tier and record moderation audit log for {moderationVendor?.name}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Verification Tier</Label>
              <select
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
                className="w-full h-9 rounded-lg border border-border bg-input px-3 text-xs font-semibold text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="SOURCED">SOURCED (UNVERIFIED) - Base Crawled Tier</option>
                <option value="CLAIMED">CLAIMED (BASIC) - Phone/Email OTP Confirmed</option>
                <option value="VERIFIED">VERIFIED - Chilean RUT Modulo 11 & SEC Validated</option>
                <option value="PREMIUM">PREMIUM (PRO) - Background Checked & Priority SLA</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="moderationNote" className="text-xs">
                Administrative Note
              </Label>
              <Textarea
                id="moderationNote"
                rows={3}
                placeholder="Document verification documentation, SEC trade certificate reference, or reason for tier adjustment..."
                value={moderationNote}
                onChange={(e) => setModerationNote(e.target.value)}
                className="bg-input text-xs"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModerationVendor(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-primary hover:bg-primary/90 text-white"
              onClick={handleConfirmModeration}
              disabled={verificationMutation.isPending}
            >
              {verificationMutation.isPending ? "Updating..." : "Save Verification Tier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

export default VendorsMarketplacePage;
